
import dayjs from 'dayjs';
import Cookies from 'universal-cookie';

import { RANGE_MINUTE } from '@/hooks/useRefresh';
import { customReport, CustomReportType } from '@/report/tam';
import { WEB_ADMIN_HOME } from '@/router/const';
import { LOGIN_TYPE, LoginInfo } from '@/store/modules/user';
import { ENV_NAME, envName } from '@/utils/getApiDomain';

export const VIDEO_APPID = 3000095;

// 子域名
const cookies = new Cookies(null, { path: '/', domain: '.dreamai.fun' });

export const loginCookieKeys = {
  // 验证码
  captcha_code: 'captcha_code',
  captcha_ticket: 'captcha_ticket',
  guid: 'guid',
  vuserid: 'vuserid',
  vusession: 'vusession',
  video_appid: 'video_appid',
  openid: 'openid',
  wx_openid: 'wx_openid',
  access_token: 'access_token',
  refresh_token: 'refresh_token',
  vsession_key: 'vsession_key',
  vuser: 'vuser',
  lastRefreshTime: 'last_refresh_time',
  lastRefreshSecond: 'last_refresh_second',
  minExpireTime: 'min_expire_time',
  name: 'name',
  avatar: 'avatar',
  env: 'env',
  main_login: 'main_login',

  // QQ
  qq_openid: 'qq_openid',
  vqq_vuserid: 'vqq_vuserid',
  vqq_vusession: 'vqq_vusession',
  vqq_appid: 'vqq_appid',
  vqq_openid: 'vqq_openid',
  vqq_access_token: 'vqq_access_token',
  vqq_refresh_token: 'vqq_refresh_token',

  // 最新标准-灰度中
  v_main_login: 'v_main_login',
  v_t_appid: 'v_t_appid',
  v_t_openid: 'v_t_openid',
  v_t_access_token: 'v_t_access_token',
  v_t_refresh_token: 'v_t_refresh_token',
  v_vuserid: 'v_vuserid',
  v_vusession: 'v_vusession',
};

const getExpiryDate = () => {
  const expiryDate = new Date();
  expiryDate.setTime(expiryDate.getTime() + (24 * 30 * 60 * 60 * 1000)); // 24 * 30 小时后的时间  30 天
  return expiryDate;
};

export const setLoginCookie = (data: LoginInfo, loginType?: LOGIN_TYPE) => {
  const guid = cookies.get(loginCookieKeys.guid);
  const { inner_token, wxuser_token_info, qquser_token_info, user_token_info } = data || {};

  const tokenInfo = {
    [LOGIN_TYPE.QQ]: { ...inner_token, ...qquser_token_info },
    [LOGIN_TYPE.WX]: { ...inner_token, ...wxuser_token_info },
    [LOGIN_TYPE.PHONE]: { ...inner_token, ...user_token_info },
  }[loginType || LOGIN_TYPE.WX];

  const expireTime = Number(tokenInfo?.expire_time || 0);

  const wxExpireTime = {
    [LOGIN_TYPE.QQ]: Number(tokenInfo?.expire_time || 0), // 特殊
    [LOGIN_TYPE.WX]: Number(tokenInfo?.access_token_expiretime || 0),
    [LOGIN_TYPE.PHONE]: Number(tokenInfo?.expire_time || 0),
  }[loginType || LOGIN_TYPE.WX];

  const env = envName();

  const minExpireTime = Math.min(expireTime, wxExpireTime);
  const time = getDayTime();

  const c = {
    [LOGIN_TYPE.QQ]: {
      vqq_vuserid: tokenInfo?.vuser,
      vqq_vusession: tokenInfo?.vsession_key,
      vqq_appid: VIDEO_APPID,
      vqq_openid: tokenInfo?.qq_openid,
      vqq_access_token: tokenInfo?.access_token,
      vqq_refresh_token: tokenInfo?.refresh_token,

      openid: tokenInfo?.qq_openid,
      qq_openid: tokenInfo?.qq_openid,
      name: tokenInfo?.qq_nick_name,
      avatar: tokenInfo?.qq_faceimage_url,

      v_t_openid: tokenInfo?.qq_openid,
    },
    [LOGIN_TYPE.WX]: {
      openid: tokenInfo?.wx_openid,
      wx_openid: tokenInfo?.wx_openid,
      name: tokenInfo?.wx_nick_name,
      avatar: tokenInfo?.wx_faceimage_url,
    },
    [LOGIN_TYPE.PHONE]: {
      openid: tokenInfo?.open_id,
      wx_openid: tokenInfo?.open_id,
      name: tokenInfo?.nick_name,
      avatar: tokenInfo?.faceimage_url,
    },
  }[loginType || LOGIN_TYPE.WX];

  const loginCookies = {
    v_main_login: loginType || LOGIN_TYPE.WX,
    v_t_appid: VIDEO_APPID,
    v_t_access_token: tokenInfo?.access_token,
    v_vuserid: tokenInfo?.vuser,
    v_vusession: tokenInfo?.vsession_key,
    v_t_refresh_token: tokenInfo?.refresh_token,

    main_login: loginType || LOGIN_TYPE.WX,
    vuserid: tokenInfo?.vuser,
    vusession: tokenInfo?.vsession_key,
    video_appid: VIDEO_APPID,
    access_token: tokenInfo?.access_token,
    refresh_token: tokenInfo?.refresh_token,
    vsession_key: tokenInfo?.vsession_key,
    vuser: tokenInfo?.vuser,
    last_refresh_time: time.timeString,
    last_refresh_second: time.seconds,
    min_expire_time: minExpireTime,
    env: env === ENV_NAME.local ? ENV_NAME.test : env,
    guid,
    ...c,
  };

  const expiryDate = getExpiryDate();
  Object.keys(loginCookies).forEach((key) => {
    const value = loginCookies?.[key as keyof typeof loginCookies];

    cookies.set(key, value, { expires: expiryDate });
  });
};

export const setLoginGuidCookies = (guid: string) => {
  const expiryDate = getExpiryDate();
  cookies.set(loginCookieKeys.guid, guid, { expires: expiryDate });
};

export const getLoginCookies = () => {
  const result: { [key: string]: string } = {};
  Object.keys(loginCookieKeys).forEach((key) => {
    const tempKey = loginCookieKeys[key as keyof typeof loginCookieKeys];
    result[key] = cookies.get(tempKey);
  });
  return result;
};

export const clearLoginCookies = () => {
  const params = getLoginCookies();
  customReport(CustomReportType.CLEAR_COOKIE, params);
  const clearCookies = loginCookieKeys;

  Object.keys(clearCookies).forEach((key) => {
    const tempKey = clearCookies[key as keyof typeof clearCookies];
    cookies.remove(tempKey);
  });
};


export const getUserId = () => cookies.get(loginCookieKeys.vuserid) || cookies.get(loginCookieKeys.vuser);

export const getDayTime = () => {
  // 获取当前时间的dayjs实例
  const currentTime = dayjs();
  const timeString = currentTime.format('YYYY-MM-DD HH:mm:ss');
  // 获取时间戳（单位为毫秒）
  const timestampInMilliseconds = currentTime.valueOf();
  // 将毫秒换算成秒
  const seconds = Math.floor(timestampInMilliseconds / 1000);
  return { timeString, seconds };
};


export const isCorrectCookie = (cookies: { [key: string]: string }) => {
  if (
    cookies.vusession
    && cookies.vuser
    // && cookies.access_token
    // && cookies.refresh_token

  // && cookies.vsession_key
  // && cookies.video_appid
  // && cookies.openid
  ) {
    return true;
  }
  return false;
};

export const inExpireTime = () => {
  const loginCookies = getLoginCookies();
  const {
    minExpireTime,
    lastRefreshSecond,
  } = loginCookies || {};
  if (isCorrectCookie(loginCookies)
    && Number(lastRefreshSecond)
    && Number(minExpireTime)) {
    const inExpireTime = getCurrentTimeDiffLastRefreshTime() > RANGE_MINUTE * 60;
    return inExpireTime;
  }
};

export const getCurrentTimeDiffLastRefreshTime = () => {
  const loginCookies = getLoginCookies();
  const {
    minExpireTime,
    lastRefreshSecond,
  } = loginCookies || {};
  try {
    const currentTime = getDayTime().seconds;
    const expireTime = Number(lastRefreshSecond) + Number(minExpireTime);
    return expireTime - currentTime;
  } catch (error) {
    return 0;
  }
};

export const setPhoneVerifyCookie = (res: {
  captcha_code: string;
  captcha_ticket: string;
}) => {
  const expiryDate = getExpiryDate();
  cookies.set('captcha_code', res.captcha_code, { expires: expiryDate });
  cookies.set('captcha_ticket', res.captcha_ticket, { expires: expiryDate });
};

export const loginReload = (pathname: string, host: string) => {
  // if (pathname.toLowerCase()
  //   .includes(PICBOOK_LOWER)) {
  //   window.location.href = `//${host}${PIC_BOOK_HOME}`;
  // } else {
  //   window.location.href = `//${host}${WEB_ADMIN_HOME}`;
  // }
  console.log('loginReload', pathname, host);
  window.location.href = `//${host}${WEB_ADMIN_HOME}`;
};
